import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './ClassFeed.css';
import Sidebar from './Sidebar';
import Header from './Header';
import ClassPostCard from './ClassPostCard';
import CreatePostDialog from './CreatePostDialog';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Announcement as AnnouncementIcon
} from '@mui/icons-material';

interface ClassPost {
  id: number;
  class_id: number;
  class_name: string;
  author_id: number;
  author_name: string;
  post_type: string;
  title: string | null;
  content: string;
  is_pinned: boolean;
  comments_count: number;
  attachments_count: number;
  attachments: any[];
  created_at: string;
  updated_at: string;
}

interface ClassInfo {
  id: number;
  name: string;
  subject: string | null;
  grade_level: string | null;
}

const ClassFeed: React.FC = () => {
  const { t } = useTranslation();
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useAuth();

  const [posts, setPosts] = useState<ClassPost[]>([]);
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated && classId) {
      fetchClassInfo();
      fetchPosts();
    }
  }, [classId, token, isAuthenticated, authLoading]);

  const fetchClassInfo = async () => {
    try {
      const response = await fetch(`/api/classes/${classId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch class info');

      const data = await response.json();
      if (data.success) {
        setClassInfo(data.class);
      }
    } catch (err: any) {
      console.error('Error fetching class info:', err);
      setError(err.message || 'Failed to load class information');
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/classes/${classId}/posts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Fetch posts response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Fetch posts error:', errorData);
        throw new Error(errorData.error || `Failed to fetch posts (${response.status})`);
      }

      const data = await response.json();
      console.log('Fetch posts data:', data);
      if (data.success) {
        setPosts(data.posts);
      }
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      setError(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (postData: any) => {
    try {
      const response = await fetch(`/api/classes/${classId}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok) throw new Error('Failed to create post');

      const data = await response.json();
      if (data.success) {
        // Add new post to the top of the list
        setPosts([data.post, ...posts]);
        setCreateDialogOpen(false);
      }
    } catch (err: any) {
      console.error('Error creating post:', err);
      throw err;
    }
  };

  const handleDeletePost = async (postId: number) => {
    try {
      const response = await fetch(`/api/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to delete post');

      // Remove post from list
      setPosts(posts.filter(p => p.id !== postId));
    } catch (err: any) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
    }
  };

  const handleTogglePin = async (postId: number) => {
    try {
      const response = await fetch(`/api/posts/${postId}/pin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to toggle pin');

      const data = await response.json();
      if (data.success) {
        // Update post in list
        setPosts(posts.map(p => p.id === postId ? data.post : p));
        // Re-sort to put pinned posts first
        setPosts(prev => [...prev].sort((a, b) => {
          if (a.is_pinned === b.is_pinned) return 0;
          return a.is_pinned ? -1 : 1;
        }));
      }
    } catch (err: any) {
      console.error('Error toggling pin:', err);
      alert('Failed to toggle pin');
    }
  };

  const handleUpdatePost = async (postId: number, updates: any) => {
    try {
      const response = await fetch(`/api/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) throw new Error('Failed to update post');

      const data = await response.json();
      if (data.success) {
        // Update post in list
        setPosts(posts.map(p => p.id === postId ? data.post : p));
      }
    } catch (err: any) {
      console.error('Error updating post:', err);
      throw err;
    }
  };

  // Separate pinned and regular posts
  const pinnedPosts = posts.filter(p => p.is_pinned);
  const regularPosts = posts.filter(p => !p.is_pinned);

  if (authLoading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <Alert severity="error">Please log in to view class feed</Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <div className="class-feed-container">
            {/* Header */}
            <div className="class-feed-header">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/classes')}
                sx={{ mb: 2 }}
              >
                {t('common.back')}
              </Button>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {classInfo?.name || 'Class Feed'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {classInfo?.subject && `${classInfo.subject} • `}
                    {classInfo?.grade_level}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  size="large"
                  sx={{ flexShrink: 0 }}
                >
                  {t('classFeed.createPost')}
                </Button>
              </Box>
            </div>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Posts */}
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <div className="class-feed-posts">
                {/* Pinned Posts */}
                {pinnedPosts.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      📌 {t('classFeed.pinnedPosts')}
                    </Typography>
                    {pinnedPosts.map(post => (
                      <ClassPostCard
                        key={post.id}
                        post={post}
                        onDelete={handleDeletePost}
                        onTogglePin={handleTogglePin}
                        onUpdate={handleUpdatePost}
                      />
                    ))}
                  </Box>
                )}

                {/* Regular Posts */}
                {regularPosts.length > 0 ? (
                  <>
                    {pinnedPosts.length > 0 && (
                      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        {t('classFeed.recentPosts')}
                      </Typography>
                    )}
                    {regularPosts.map(post => (
                      <ClassPostCard
                        key={post.id}
                        post={post}
                        onDelete={handleDeletePost}
                        onTogglePin={handleTogglePin}
                        onUpdate={handleUpdatePost}
                      />
                    ))}
                  </>
                ) : pinnedPosts.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <AnnouncementIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {t('classFeed.noPosts')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {t('classFeed.noPostsDescription')}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateDialogOpen(true)}
                    >
                      {t('classFeed.createFirstPost')}
                    </Button>
                  </Box>
                ) : null}
              </div>
            )}

            {/* Floating Action Button for mobile */}
            <Fab
              color="primary"
              aria-label="add post"
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                display: { xs: 'flex', md: 'none' }
              }}
              onClick={() => setCreateDialogOpen(true)}
            >
              <AddIcon />
            </Fab>
          </div>
        </div>
      </div>

      {/* Create Post Dialog */}
      <CreatePostDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreatePost}
        classId={parseInt(classId || '0')}
      />
    </div>
  );
};

export default ClassFeed;

